import React, { useState, useEffect } from 'react';
import { Search, Calendar, User, Edit, Trash2, Copy, FileDown, ChevronDown, Clock, MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import { generateQuotePDF } from './utils/pdfGenerator';

const QuoteListView = ({ onLoadQuote, user }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Removed deleteDialog state - using browser confirm instead
  const [exportDropdown, setExportDropdown] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/api/quotes`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('Not authenticated - redirecting to login');
          localStorage.removeItem('authToken');
          window.location.href = `${API_URL}/saml/login`;
        }
        setQuotes([]);
        return;
      }

      const data = await response.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (quote) => {
    try {
      const copiedQuote = {
        ...quote.quote_data,
        customerName: `${quote.customer_name} (Copy)`,
      };
      onLoadQuote({ ...quote, quote_data: copiedQuote }, true); // true = new quote
    } catch (error) {
      console.error('Error copying quote:', error);
    }
  };

  const handleExport = async (quote, type) => {
    try {
      if (type === 'internal') {
        generateQuotePDF(quote, 'internal');
      } else {
        generateQuotePDF(quote, 'customer');
      }
      const message = type === 'customer' 
        ? `Customer PDF downloaded for ${quote.quote_number}` 
        : `Internal PDF downloaded for ${quote.quote_number}`;
      alert(message);
      setExportDropdown(null);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleDelete = async (quoteId, quoteNumber) => {
    if (!window.confirm(`Are you sure you want to delete quote ${quoteNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/api/quotes/${quoteId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete quote');
      }

      // Refresh list
      fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote. Please try again.');
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quote.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quote.opportunity_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-700',
      pending_approval: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      final: 'bg-blue-100 text-blue-700',
      sent: 'bg-purple-100 text-purple-700',
      accepted: 'bg-green-100 text-green-700',
      lost: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.draft;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      rejected: 'Rejected',
      final: 'Final',
      sent: 'Sent',
      accepted: 'Won',
      lost: 'Lost'
    };
    return labels[status] || status;
  };

  const handleSetOutcome = async (quoteId, outcome, lostReason = null) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/outcome`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ outcome, lostReason })
      });

      if (!response.ok) {
        throw new Error('Failed to update quote outcome');
      }

      // Refresh the list
      fetchQuotes();
    } catch (error) {
      console.error('Error setting outcome:', error);
      alert('Failed to update quote outcome. Please try again.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Quotes</h1>
        <p className="text-slate-600">Manage and review all quotes</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex gap-4">
        <div className="flex-1 relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by quote #, customer, or opportunity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="final">Final</option>
          <option value="sent">Sent</option>
          <option value="accepted">Won</option>
          <option value="lost">Lost</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Quote List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading quotes...</div>
        ) : filteredQuotes.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {searchTerm || statusFilter !== 'all' ? 'No quotes match your filters' : 'No quotes yet'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed min-w-[900px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 w-[130px]">Quote #</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 w-[180px]">Customer</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 w-[100px]">Industry</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 w-[100px]">Created</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 w-[120px]">Created By</th>
                  <th className="text-right px-4 py-4 text-sm font-semibold text-slate-700 w-[110px]">Total ARR</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-700 w-[80px]">Status</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-700 w-[130px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote) => (
                  <tr key={quote.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="font-mono text-sm font-semibold text-blue-600 truncate">{quote.quote_number}</div>
                      {quote.opportunity_id && (
                        <div className="text-xs text-slate-500 mt-1 truncate" title={quote.opportunity_id}>Opp: {quote.opportunity_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-800 break-words line-clamp-2" title={quote.customer_name}>
                        {quote.customer_name || <span className="text-slate-400 italic">No customer</span>}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-slate-600 truncate" title={quote.customer_industry}>{quote.customer_industry || '-'}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar size={14} className="flex-shrink-0" />
                        <span>{new Date(quote.created_at).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <User size={14} className="flex-shrink-0" />
                        <span className="truncate" title={quote.created_by_name}>{quote.created_by_name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="text-base font-bold text-green-600 whitespace-nowrap">
                        ${parseFloat(quote.total_arr).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(quote.status)}`}>
                          {quote.status === 'pending_approval' && <Clock size={12} />}
                          {quote.status === 'accepted' && <CheckCircle size={12} />}
                          {quote.status === 'lost' && <XCircle size={12} />}
                          {getStatusLabel(quote.status)}
                        </span>
                        {quote.rejection_feedback && quote.status === 'draft' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs" title={quote.rejection_feedback}>
                            <MessageSquare size={10} />
                            Feedback
                          </span>
                        )}
                        {quote.lost_reason && quote.status === 'lost' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs" title={quote.lost_reason}>
                            <MessageSquare size={10} />
                            Reason
                          </span>
                        )}
                        {/* Outcome selector for sent quotes */}
                        {quote.status === 'sent' && (
                          <div className="flex gap-1 mt-1">
                            <button
                              onClick={() => handleSetOutcome(quote.id, 'accepted')}
                              className="px-2 py-0.5 bg-green-100 text-green-700 hover:bg-green-200 rounded text-xs font-medium transition-colors"
                              title="Mark as Won"
                            >
                              Won
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt('Please enter the reason for loss (optional):');
                                handleSetOutcome(quote.id, 'lost', reason);
                              }}
                              className="px-2 py-0.5 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs font-medium transition-colors"
                              title="Mark as Lost"
                            >
                              Lost
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onLoadQuote(quote)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Quote"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleCopy(quote)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Copy Quote"
                        >
                          <Copy size={16} />
                        </button>

                        {/* Export Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setExportDropdown(exportDropdown === quote.id ? null : quote.id)}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center"
                            title="Export Quote"
                          >
                            <FileDown size={16} />
                            <ChevronDown size={12} />
                          </button>

                          {exportDropdown === quote.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
                              <div className="py-1">
                                <button
                                  onClick={() => handleExport(quote, 'internal')}
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 flex flex-col"
                                >
                                  <span className="font-semibold text-slate-800 text-sm">Internal PDF</span>
                                  <span className="text-xs text-slate-500">With SKUs & details</span>
                                </button>
                                <button
                                  onClick={() => handleExport(quote, 'customer')}
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 flex flex-col border-t border-slate-100"
                                >
                                  <span className="font-semibold text-slate-800 text-sm">Customer PDF</span>
                                  <span className="text-xs text-slate-500">Summary view</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleDelete(quote.id, quote.quote_number)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Quote"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteListView;
